import { toValue, type MaybeRefOrGetter } from 'vue'
import type { MetaObject } from 'nuxt/schema'
import { useSeoCanonicalUrl } from './useSeoJsonLd'

type SeoHeadOptions = Omit<MetaObject, 'title' | 'meta' | 'link'> & {
  title?: MaybeRefOrGetter<string>
  description?: MaybeRefOrGetter<string>
  robots?: MaybeRefOrGetter<string>
  image?: MaybeRefOrGetter<string>
  imageAlt?: MaybeRefOrGetter<string>
  ogType?: MaybeRefOrGetter<string>
  publishedAtIso?: MaybeRefOrGetter<string>
  modifiedAtIso?: MaybeRefOrGetter<string>
  categoryName?: MaybeRefOrGetter<string>
  keywords?: MaybeRefOrGetter<string>
  includeSocial?: boolean
  includeCurrentHreflang?: boolean
  meta?: MetaObject['meta']
  link?: MetaObject['link']
}

type SeoMeta = {
  key: string
  content: string
  name?: string
  property?: string
}

const sortSeoMeta = <T extends { key?: unknown }>(items: T[]) => {
  return items
    .map((item, index) => ({ item, index }))
    .toSorted((a, b) => {
      const aKey = a.item.key
      const bKey = b.item.key
      if ((aKey === null || aKey === undefined) && (bKey === null || bKey === undefined)) return a.index - b.index
      if (aKey === null || aKey === undefined) return 1
      if (bKey === null || bKey === undefined) return -1
      return String(aKey).localeCompare(String(bKey)) || a.index - b.index
    })
    .map(({ item }) => item)
}

const DEFAULT_SOCIAL_IMAGE =
  'https://resources1.interface003.com/web30/2026/08/12/638b20d9-edbd-40f3-904a-b1866b172c7e.png'

const resolveValue = <T>(value: MaybeRefOrGetter<T> | undefined, fallback: T): T => {
  if (value === undefined) return fallback
  return toValue(value)
}

export const useSeoHead = (options: SeoHeadOptions = {}) => {
  const route = useRoute()
  const { localeMeta } = useAppI18n()
  const canonicalUrl = useSeoCanonicalUrl()

  const resolveImageUrl = (image: string) => {
    if (!image) return DEFAULT_SOCIAL_IMAGE
    try {
      return new URL(image, canonicalUrl.value).href
    } catch {
      return DEFAULT_SOCIAL_IMAGE
    }
  }

  useHead(() => {
    const {
      description: _description,
      robots: _robots,
      image: _image,
      imageAlt: _imageAlt,
      ogType: _ogType,
      publishedAtIso: _publishedAtIso,
      modifiedAtIso: _modifiedAtIso,
      categoryName: _categoryName,
      keywords: _keywords,
      includeSocial: _includeSocial,
      includeCurrentHreflang: _includeCurrentHreflang,
      meta: extraMeta,
      link: extraLink,
      ...passthrough
    } = options
    const title = resolveValue(options.title, '')
    const description = resolveValue(options.description, '')
    const normalizedPath = route.path.toLowerCase().replace(/\/+$/, '') || '/'
    const localePrefix = `/${localeMeta.code.toLowerCase()}`
    const isAccountPath =
      normalizedPath === '/account' ||
      normalizedPath.startsWith('/account/') ||
      normalizedPath === `${localePrefix}/account` ||
      normalizedPath.startsWith(`${localePrefix}/account/`)
    const robots = resolveValue(options.robots, isAccountPath ? 'noindex,nofollow' : 'index,follow')
    const image = options.includeSocial ? resolveImageUrl(resolveValue(options.image, '')) : ''
    const imageAlt = resolveValue(options.imageAlt, title)
    const publishedAtIso = resolveValue(options.publishedAtIso, '')
    const modifiedAtIso = resolveValue(options.modifiedAtIso, '')
    const categoryName = resolveValue(options.categoryName, '')
    const keywords = resolveValue(options.keywords, '')
    const meta: SeoMeta[] = [
      { key: 'description', name: 'description', content: description },
      { key: 'robots', name: 'robots', content: robots },
    ]

    if (keywords) {
      meta.push({ key: 'keywords', name: 'keywords', content: keywords })
    }

    if (options.includeSocial) {
      meta.push(
        { key: 'og:type', property: 'og:type', content: resolveValue(options.ogType, 'article') },
        { key: 'og:url', property: 'og:url', content: canonicalUrl.value },
        { key: 'og:title', property: 'og:title', content: title },
        { key: 'og:description', property: 'og:description', content: description },
        { key: 'og:locale', property: 'og:locale', content: localeMeta.language },
        { key: 'og:site_name', property: 'og:site_name', content: 'WikiBit' },
        { key: 'twitter:card', name: 'twitter:card', content: 'summary_large_image' },
        { key: 'twitter:title', name: 'twitter:title', content: title },
        { key: 'twitter:description', name: 'twitter:description', content: description },
        { key: 'twitter:image:alt', name: 'twitter:image:alt', content: imageAlt },
      )

      if (image) {
        meta.push(
          { key: 'og:image', property: 'og:image', content: image },
          { key: 'og:image:alt', property: 'og:image:alt', content: imageAlt },
          { key: 'twitter:image', name: 'twitter:image', content: image },
        )
      }

      if (publishedAtIso) {
        meta.push({ key: 'og:published_at_iso', property: 'og:published_at_iso', content: publishedAtIso })
      }
      if (modifiedAtIso) {
        meta.push({ key: 'og:modified_at_iso', property: 'og:modified_at_iso', content: modifiedAtIso })
      }
      if (categoryName) {
        meta.push({ key: 'og:category_name', property: 'og:category_name', content: categoryName })
      }
    }

    return {
      ...passthrough,
      title: title || undefined,
      link: [
        { key: 'canonical', rel: 'canonical', href: canonicalUrl.value },
        ...(options.includeCurrentHreflang
          ? [{ key: 'hreflang-current', rel: 'alternate', hreflang: localeMeta.language, href: canonicalUrl.value }]
          : []),
        ...(extraLink || []),
      ],
      meta: sortSeoMeta([...(extraMeta || []), ...meta]),
    }
  })
}

export const extractFirstImageUrl = (html: string) => {
  const match = html.match(/<img\b[^>]*\bsrc=["']([^"']+)["']/i)
  return match?.[1]?.trim() || ''
}
